import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "@/components/Layout";
import {
  Mail, Send, Loader2, Users as UsersIcon, AlertTriangle, CheckCircle2,
  Globe2, FlaskConical, Eye, History,
} from "lucide-react";

/**
 * Phase 11 — Marketing Composer.
 *
 * Single-page admin tool for sending bulk marketing emails to:
 *   - Active customers with `marketing_opt_in = true`
 *   - Geo-leads waitlist (anyone who left their email on a blocked region)
 *   - Or both (union, dedup'ed)
 *
 * Live preview pane (right) renders the actual email HTML via
 * GET /admin/marketing/preview-template — so the operator sees exactly what
 * recipients will receive before pressing send.
 */
export default function Marketing() {
  const [audience, setAudience] = useState("all");
  const [preview, setPreview]   = useState(null); // {estimated, breakdown, mocked}
  const [busy, setBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [mockMode, setMockMode] = useState(false);

  const [subject, setSubject] = useState("LUMEN — Bu Hafta Yayında");
  const [bodyHtml, setBodyHtml] = useState(
    "<p>Merhaba,</p><p>Bu hafta kütüphanemize katılan <strong>Premier Lig canlı maçları</strong> ve <strong>4K dizi koleksiyonumuzu</strong> kaçırmayın.</p><p>Aboneliğinizden en iyi şekilde yararlanmak için hemen izlemeye başlayın.</p>"
  );
  const [ctaUrl, setCtaUrl]     = useState("https://lumen.tv/watch");
  const [ctaLabel, setCtaLabel] = useState("Hemen İzle");

  const refreshPreview = async (aud = audience) => {
    try {
      const r = await api.get(`/saas/admin/marketing/preview?audience=${aud}`);
      setPreview(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Önizleme alınamadı.");
    }
  };

  const refreshHistory = async () => {
    try {
      const r = await api.get("/saas/admin/marketing/history?limit=8");
      setHistory(r.data.history || []);
      setMockMode(!!r.data.mock_mode);
    } catch { /* silent */ }
  };

  useEffect(() => { refreshPreview(); refreshHistory(); }, []);
  useEffect(() => { refreshPreview(audience); /* eslint-disable-next-line */ }, [audience]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setResult(null);
    if (!subject.trim() || !bodyHtml.trim()) {
      setErr("Konu ve içerik zorunlu.");
      return;
    }
    if (!window.confirm(`${preview?.estimated || 0} kişiye e-posta gönderilecek. Onaylıyor musunuz?`)) {
      return;
    }
    setSendBusy(true);
    try {
      const r = await api.post("/saas/admin/marketing/send-bulk", {
        subject:   subject.trim(),
        body_html: bodyHtml.trim(),
        audience,
        cta_url:   ctaUrl.trim() || null,
        cta_label: ctaLabel.trim() || null,
      });
      setResult(r.data);
      refreshHistory();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Gönderim başarısız.");
    } finally { setSendBusy(false); }
  };

  // Build preview iframe URL (template endpoint returns rendered HTML)
  const tplBaseUrl = (process.env.REACT_APP_BACKEND_URL || "") +
    "/api/saas/admin/marketing/preview-template";
  const params = new URLSearchParams({
    kind:      "custom",
    subject:   subject.slice(0, 80),
    body_html: bodyHtml.slice(0, 1500),
  });
  const previewSrc = `${tplBaseUrl}?${params.toString()}`;

  return (
    <AdminLayout
      title="Marketing"
      breadcrumb={["Sistem", "Marketing"]}
    >
      <div className="space-y-6 max-w-7xl" data-testid="admin-marketing-page">
        {/* Mock-mode banner */}
        {mockMode && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm" data-testid="marketing-mock-banner">
            <FlaskConical className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <strong>MOCK Mode — RESEND_API_KEY tanımlı değil.</strong>
              <p className="text-xs text-amber-200/80 mt-0.5">Gönderim her satıra log düşer ama gerçek e-posta YOLLAMAZ. Production'da .env'e <code className="font-mono">RESEND_API_KEY</code> ekleyin.</p>
            </div>
          </div>
        )}

        {/* Audience cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="marketing-audience-cards">
          <AudienceCard
            label="Tüm Hedef"
            count={preview?.estimated ?? "—"}
            active={audience === "all"}
            onClick={() => setAudience("all")}
            icon={UsersIcon}
            testid="aud-all"
          />
          <AudienceCard
            label="Müşteriler (opt-in)"
            count={preview?.breakdown?.customers ?? "—"}
            active={audience === "customers"}
            onClick={() => setAudience("customers")}
            icon={Mail}
            testid="aud-customers"
          />
          <AudienceCard
            label="Waitlist"
            count={preview?.breakdown?.waitlist ?? "—"}
            active={audience === "waitlist"}
            onClick={() => setAudience("waitlist")}
            icon={Globe2}
            testid="aud-waitlist"
          />
          <div className="bg-slate-950/40 border border-slate-800 rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold flex items-center gap-1.5">
              <FlaskConical className="w-3 h-3" /> Resend Mod
            </div>
            <div className={`text-lg font-bold mt-1 ${mockMode ? "text-amber-400" : "text-emerald-400"}`}>
              {mockMode ? "MOCK" : "LIVE"}
            </div>
          </div>
        </div>

        {/* Composer + Preview side-by-side */}
        <div className="grid lg:grid-cols-2 gap-6">
          <form
            onSubmit={submit}
            className="bg-slate-950/60 border border-slate-800 rounded-2xl p-6 space-y-4"
            data-testid="marketing-form"
          >
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-amber-400" />
              <h2 className="text-base font-bold text-white">Marketing Composer</h2>
            </div>

            <Field label="Konu">
              <input
                type="text" maxLength={120}
                value={subject} onChange={(e) => setSubject(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white text-sm focus:border-amber-500 outline-none"
                data-testid="marketing-subject"
              />
            </Field>
            <Field label="İçerik (HTML / Markdown)">
              <textarea
                rows={9} maxLength={4000}
                value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white text-sm focus:border-amber-500 outline-none resize-none font-mono"
                data-testid="marketing-body"
              />
              <p className="text-[10px] text-gray-500 mt-1">{bodyHtml.length}/4000 karakter · &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;a href&gt; gibi temel HTML etiketleri desteklenir</p>
            </Field>

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="CTA Bağlantısı (opsiyonel)">
                <input
                  type="text"
                  value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white text-sm focus:border-amber-500 outline-none font-mono"
                  data-testid="marketing-cta-url"
                />
              </Field>
              <Field label="CTA Yazısı">
                <input
                  type="text" maxLength={32}
                  value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white text-sm focus:border-amber-500 outline-none"
                  data-testid="marketing-cta-label"
                />
              </Field>
            </div>

            {err && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs" data-testid="marketing-err">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {err}
              </div>
            )}
            {result && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs" data-testid="marketing-result">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  Gönderildi: <strong className="text-white">{result.sent}</strong>
                  {" "}· Başarısız: <strong className="text-white">{result.failed}</strong>
                  {" "}· Mock: <strong className="text-white">{result.mocked}</strong>
                  {" "}(toplam {result.total})
                </span>
              </div>
            )}

            <div className="flex items-center justify-between pt-3 border-t border-slate-800">
              <p className="text-[11px] text-gray-500">
                <strong className="text-amber-400">{preview?.estimated ?? 0}</strong> kişiye gönderilecek
              </p>
              <button
                type="submit"
                disabled={sendBusy}
                className="h-11 px-5 rounded-lg bg-amber-500 text-black font-bold text-sm hover:bg-amber-400 transition disabled:opacity-50 inline-flex items-center gap-2 active:translate-y-[1px]"
                data-testid="marketing-send"
              >
                {sendBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sendBusy ? "Gönderiliyor…" : "Toplu Gönderimi Başlat"}
              </button>
            </div>
          </form>

          {/* Live preview */}
          <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4 flex flex-col" data-testid="marketing-preview">
            <div className="flex items-center justify-between gap-2 px-2 pb-3 border-b border-slate-800 mb-3">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-amber-400" />
                <h2 className="text-sm font-bold text-white">Canlı Önizleme</h2>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">to: customer@example.com</span>
            </div>
            <iframe
              key={`${subject}-${bodyHtml.length}`}
              src={previewSrc}
              title="email-preview"
              sandbox="allow-same-origin"
              className="w-full flex-1 min-h-[480px] rounded-lg bg-[#0b0b10] border border-slate-800"
              data-testid="marketing-preview-iframe"
            />
          </div>
        </div>

        {/* History */}
        {history.length > 0 && (
          <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-6" data-testid="marketing-history">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-4 h-4 text-amber-400" />
              <h2 className="text-base font-bold text-white">Son Gönderimler</h2>
            </div>
            <div className="divide-y divide-slate-800">
              {history.map((h, i) => (
                <div key={i} className="py-3 flex items-start gap-3 text-sm">
                  <Mail className="w-3.5 h-3.5 text-amber-400 mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <strong className="text-white truncate block">{h.subject}</strong>
                    <p className="text-[11px] font-mono text-gray-500 mt-0.5">
                      {new Date(h.created_at).toLocaleString()} · {h.audience} · {h.admin}
                    </p>
                  </div>
                  <div className="text-[10px] font-mono text-right shrink-0 leading-tight">
                    <span className="text-emerald-400">{h.result?.sent ?? 0}</span>
                    {" "}/<span className="text-red-400"> {h.result?.failed ?? 0}</span>
                    {(h.result?.mocked || 0) > 0 && <div className="text-amber-400">mock {h.result.mocked}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function AudienceCard({ label, count, active, onClick, icon: Icon, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left p-4 rounded-xl border transition ${
        active
          ? "bg-amber-500/10 border-amber-500/50 shadow-lg shadow-amber-500/10"
          : "bg-slate-950/40 border-slate-800 hover:border-slate-700"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-gray-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`text-3xl font-bold mt-1 ${active ? "text-amber-400" : "text-white"}`}>{count}</div>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
